import { cn } from '@/lib/utils';
import { MapPin, Video } from 'lucide-react';
import { ReactNode, useState } from 'react';

interface ServiceModalityProps {
	data: {
		service_modality: Professionals.ServiceModality;
	};
	selectedModality?: Professionals.ServiceModality;
	address?: Clinics.Address;
	onModalityChange?: (modality: Professionals.ServiceModality) => void;
	addDescription?: boolean;
	defaultModality?: Professionals.ServiceModality;
	disabled?: boolean;
}

interface AddressSectionProps {
	addressInfo: Clinics.Address;
	icon: ReactNode;
	className?: string;
}
const AddressSection = ({
	addressInfo,
	icon,
	className,
}: AddressSectionProps) => (
	<div
		className={cn(
			'flex flex-col gap-1 rounded-lg transition-all duration-300',
			className,
		)}
	>
		<div className="flex items-center gap-1">
			{icon}
			<h4 className="text-sm font-semibold text-dark">Endereço</h4>
		</div>
		<p className="text-sm text-zinc-700">
			<span className="text-sm font-semibold text-dark">Rua:</span>{' '}
			{addressInfo.street_name}
		</p>
		<p className="text-sm text-zinc-700">
			<span className="text-sm font-semibold text-dark">Bairro:</span>{' '}
			{addressInfo.district_name}
		</p>
		<p className="text-sm text-zinc-700">
			<span className="text-sm font-semibold text-dark">Número:</span>{' '}
			{addressInfo.number}
		</p>
		<p className="text-sm text-zinc-700">
			<span className="text-sm font-semibold text-dark">Cidade:</span>{' '}
			{addressInfo.city}
		</p>
		<p className="text-sm text-zinc-700">
			<span className="text-sm font-semibold text-dark">Estado:</span>{' '}
			{addressInfo.state}
		</p>
		<p className="text-sm text-zinc-700">
			<span className="text-sm font-semibold text-dark">Referência:</span>{' '}
			{addressInfo.ref}
		</p>
	</div>
);

const ServiceModalityFeedback = ({
	data,
	address,
	onModalityChange,
	addDescription = true,
	defaultModality,
	disabled = false,
}: ServiceModalityProps) => {
	const [selectedModality, setSelectedModality] =
		useState<Professionals.ServiceModality>(
			defaultModality || 'FULL_REMOTE',
		);

	if (!data) return null;

	const handleModalityChange = (modality: Professionals.ServiceModality) => {
		setSelectedModality(modality);
		if (onModalityChange) {
			onModalityChange(modality);
		}
	};

	switch (data.service_modality) {
		case 'FULL_PRESENCIAL':
			return (
				<div className="flex w-full rounded-lg bg-zinc-100 p-4">
					<div className="space-y-1">
						<div>
							<div>
								<h3 className="text-sm text-dark">
									Este profissional só atende presencialmente.
								</h3>
								<h3 className="text-md font-semibold text-dark">
									Confira o endereço de atendimento
								</h3>
							</div>
						</div>
						{address && (
							<AddressSection
								icon={
									<MapPin className="hidden w-4 md:block" />
								}
								addressInfo={address}
							/>
						)}
					</div>
				</div>
			);

		case 'FULL_REMOTE':
			if (!addDescription) return;

			return (
				<div className="flex w-full items-start gap-3 rounded-2xl border border-zinc-100 bg-zinc-50 px-3 py-2.5">
					<div className="flex shrink-0 items-center justify-center rounded-full bg-zinc-200/80 p-2">
						<Video
							className="size-5 text-zinc-600"
							strokeWidth={2}
						/>
					</div>
					<div className="space-y-0.5 text-xs text-zinc-600 md:text-sm">
						<p className="font-medium text-zinc-700">
							Este profissional atende exclusivamente online.
						</p>
						<p className="text-zinc-500">
							A consulta acontecerá aqui na Plumaa. Na hora
							marcada, entre pelo menu{' '}
							<span className="font-medium text-zinc-600">
								Início
							</span>{' '}
							e toque em{' '}
							<span className="font-medium text-zinc-600">
								Acessar
							</span>{' '}
							na sua consulta.
						</p>
					</div>
				</div>
			);

		case 'HYBRID':
			return (
				<div className="flex w-full rounded-lg bg-zinc-100 p-4">
					<div className="w-full space-y-3">
						{addDescription && (
							<div>
								<h3 className="text-sm text-dark">
									Este profissional atende de maneira híbrida.
								</h3>
								<h3 className="text-md font-semibold text-dark">
									Como você quer ser atendido?
								</h3>
							</div>
						)}

						<div className="relative flex w-full rounded-full bg-zinc-200">
							<button
								className={`relative z-10 w-1/2 py-2 text-center text-sm font-medium transition-colors duration-300 focus:outline-none focus:ring-0 ${
									selectedModality === 'FULL_REMOTE'
										? 'text-white'
										: 'text-zinc-600'
								}`}
								onClick={() =>
									handleModalityChange('FULL_REMOTE')
								}
								disabled={disabled}
							>
								Online
							</button>
							<button
								className={`relative z-10 w-1/2 py-2 text-center text-sm font-medium transition-colors duration-300 focus:outline-none focus:ring-0 ${
									selectedModality === 'FULL_PRESENCIAL'
										? 'text-white'
										: 'text-zinc-600'
								}`}
								onClick={() =>
									handleModalityChange('FULL_PRESENCIAL')
								}
								disabled={disabled}
							>
								Presencial
							</button>
							<div
								className={`absolute bottom-0 left-0 right-0 top-0 w-1/2 rounded-full bg-zinc-800 transition-all duration-300 ${
									selectedModality === 'FULL_REMOTE'
										? 'translate-x-0'
										: 'translate-x-full'
								}`}
							/>
						</div>

						{selectedModality === 'FULL_PRESENCIAL' ? (
							<div className="animate-fadeIn">
								{address && (
									<AddressSection
										icon={
											<MapPin className="hidden w-4 md:block" />
										}
										addressInfo={address}
									/>
								)}
							</div>
						) : (
							<div className="animate-fadeIn flex items-start gap-3 rounded-lg bg-zinc-100 p-2.5">
								<div className="flex shrink-0 items-center justify-center rounded-full bg-zinc-200/80 p-2">
									<Video
										className="size-5 text-zinc-600"
										strokeWidth={2}
									/>
								</div>
								<p className="text-sm text-dark text-zinc-600">
									A consulta acontecerá aqui na Plumaa. Na
									hora marcada, entre pelo menu{' '}
									<span className="font-medium text-zinc-700">
										Início
									</span>{' '}
									e toque em{' '}
									<span className="font-medium text-zinc-700">
										Acessar
									</span>{' '}
									na sua consulta.
								</p>
							</div>
						)}
					</div>
				</div>
			);

		default:
			return null;
	}
};

export { ServiceModalityFeedback, AddressSection };
